import React, { useCallback } from 'react';
import { Input, Select, InputNumber, DatePicker, TimePicker, Upload, Button, Table, Modal, Form, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Field } from 'formik';
import dayjs from 'dayjs';
import { useTalepListesi } from '../../hooks/useFormSchema';
import { API_BASE_URL } from '../../utils/config';
import { inputNumberMoneyProps } from '../../utils/currency';
import { trIncludes } from '../../utils/trText';
import { cihazKonumuAl, konumKontrol } from '../../utils/cihazKonumu';

const { TextArea } = Input;
const { Option } = Select;

const decodeHtmlEntities = (text) => {
  if (typeof text !== 'string') return text;
  
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const renderLabel = (label) => {
  if (typeof label === 'string' && (label.includes('<') && label.includes('>'))) {
    const allowedTags = ['small', 'em', 'strong', 'b', 'i', 'u', 'span'];
    const cleanLabel = label.replace(/<(?!\/?(?:small|em|strong|b|i|u|span)(?:\s[^>]*)?>)[^>]*>/gi, '');
    return <span dangerouslySetInnerHTML={{ __html: cleanLabel }} />;
  }
  if (typeof label === 'string') {
    return decodeHtmlEntities(label);
  }
  return label;
};

const renderOptionLabel = (label) => {
  if (typeof label === 'string' && (label.includes('<') && label.includes('>'))) {
    const allowedTags = ['small', 'em', 'strong', 'b', 'i', 'u', 'span'];
    const cleanLabel = label.replace(/<(?!\/?(?:small|em|strong|b|i|u|span)(?:\s[^>]*)?>)[^>]*>/gi, '');
    return <span dangerouslySetInnerHTML={{ __html: cleanLabel }} />;
  }
  if (typeof label === 'string') {
    return decodeHtmlEntities(label);
  }
  return label;
};

const getOptionsFromValue = (fieldConfig) => {
  if (fieldConfig.name === 'kullanici_surucu') {
    if (fieldConfig.value === 0 || fieldConfig.value === '0' || fieldConfig.value === undefined || fieldConfig.value === null) {
      return [];
    }
    
    if (fieldConfig.query && fieldConfig.value && typeof fieldConfig.value === 'string') {
      if (fieldConfig.value.includes(',')) {
        return fieldConfig.value.split(',').map(option => {
          if (option.includes(':')) {
            const [label, value] = option.split(':');
            return { label: label.trim(), value: value.trim() };
          }
          return { label: option.trim(), value: option.trim() };
        });
      }
      
      if (fieldConfig.value.includes(':')) {
        const [label, value] = fieldConfig.value.split(':');
        return [{ label: label.trim(), value: value.trim() }];
      }
    }
    
    if (fieldConfig.options && Array.isArray(fieldConfig.options)) {
      return fieldConfig.options;
    }
    
    return [];
  }
  
  if (fieldConfig.query && fieldConfig.value && typeof fieldConfig.value === 'string') {
    if (fieldConfig.value.includes(',')) {
      return fieldConfig.value.split(',').map(option => {
        if (option.includes(':')) {
          const [label, value] = option.split(':');
          return { label: label.trim(), value: value.trim() };
        }
        return { label: option.trim(), value: option.trim() };
      });
    }
    
    if (fieldConfig.value.includes(':')) {
      const [label, value] = fieldConfig.value.split(':');
      return [{ label: label.trim(), value: value.trim() }];
    }
  }
  
  if (fieldConfig.options && Array.isArray(fieldConfig.options)) {
    return fieldConfig.options;
  }
  
  return [];
};

//Input Field
const FormikInputFieldComponent = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onBlur } = field;
  const { touched, errors } = form;
  const hasError = touched[name] && errors[name];

  const handleChange = useCallback((e) => {
    const newValue = e.target ? e.target.value : e;
    setFieldValue(name, newValue, false);
  }, [name, setFieldValue]);

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <Input
        id={name}
        name={name}
        value={value || ''}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={fieldConfig.placeholder}
        disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
        status={hasError ? 'error' : ''}
        style={{
          backgroundColor: darkMode ? '#262626' : '#fff',
          borderColor: darkMode ? '#434343' : '#d9d9d9',
          color: darkMode ? '#fff' : '#000'
        }}
      />
    </div>
  );
};

export const FormikInputField = React.memo(FormikInputFieldComponent);

// TextArea Field
export const FormikTextAreaField = React.memo(({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onBlur } = field;
  const { touched, errors } = form;

  const hasError = touched[name] && errors[name];

  const handleChange = useCallback((e) => {
    const newValue = e.target ? e.target.value : e;
    setFieldValue(name, newValue, false);
  }, [name, setFieldValue]);

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <TextArea
        id={name}
        name={name}
        value={value || ''}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={fieldConfig.placeholder}
        disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
        rows={fieldConfig.rows || 4}
        status={hasError ? 'error' : ''}
        style={{
          backgroundColor: darkMode ? '#262626' : '#fff',
          borderColor: darkMode ? '#434343' : '#d9d9d9',
          color: darkMode ? '#fff' : '#000'
        }}
      />
    </div>
  );
});

//Select Field
export const FormikSelectField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;
  const hasError = touched[name] && errors[name];

  const getSelectValue = () => {
    if (!value) return undefined;

    if (typeof value === 'string' && value.includes(':Seçiniz')) {
      return undefined;
    }

    if (typeof value === 'string' && value.includes(',')) {
      const firstOption = value.split(',')[0];
      if (firstOption.includes(':')) {
        return firstOption.split(':')[0].trim();
      }
      return firstOption.trim();
    }

    if (typeof value === 'string' && value.includes(':')) {
      const key = value.split(':')[0].trim();
      if (key === '0' && fieldConfig.options && fieldConfig.options.length > 1) {
        return undefined;
      }
      return key;
    }
    
    if ((value === '0' || value === 0) && fieldConfig.options && fieldConfig.options.length > 1) {
      return undefined;
    }
    
    if (fieldConfig.options && fieldConfig.options.length > 0) {
      const valueExists = fieldConfig.options.some(option => String(option.value) === String(value));
      if (!valueExists) {
        return fieldConfig.options[0].value;
      }
    }
    
    return value;
  };

  const getType1Checked = () => {
    if (fieldConfig.type === 1) {
      return value === 1 || value === '1';
    }
    return false;
  };

  const handleChange = (selectedValue) => {
    setFieldValue(name, selectedValue);
    if (name === 'il' || name === 'ilce') {
      setFieldValue('lokasyon', selectedValue);
    }
    if(name === 'arac_id') {
      setFieldValue('arac_id', selectedValue);
    }
    if (name === 'banka-pass') {
      setFieldValue('banka-pass', selectedValue);
    }
  };

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      {fieldConfig.type === 1 ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id={name}
            name={name}
            checked={getType1Checked()}
            onChange={(e) => setFieldValue(name, e.target.checked ? 1 : 0)}
            disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
            style={{
              marginRight: '8px',
              transform: 'scale(1.2)'
            }}
          />
          <span style={{ color: darkMode ? '#fff' : '#000' }}>
            {fieldConfig.placeholder || fieldConfig.label}
          </span>
        </div>
      ) : (
        <Select
          id={name}
          name={name}
          value={getSelectValue()}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={fieldConfig.placeholder || 'Seçiniz'}
          disabled={fieldConfig.readOnly || (fieldConfig.attr && (fieldConfig.attr === "disabled" || fieldConfig.attr.includes("disabled")))}
          status={hasError ? 'error' : ''}
          style={{
            width: '100%',
            backgroundColor: darkMode ? '#262626' : '#fff',
            color: darkMode ? '#fff' : '#000'
          }}
          showSearch={fieldConfig.attr && !fieldConfig.attr.includes("disabled")}
          filterOption={(input, option) =>
            trIncludes(option?.children, input)
          }
          styles={{
            popup: {
              root: {
                backgroundColor: darkMode ? '#1f1f1f' : '#fff',
                border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
              }
            }
          }}
          open={fieldConfig.attr && fieldConfig.attr.includes("disabled") ? false : undefined}
        >
          {fieldConfig.options?.map((option) => (
            <Option key={option.value} value={option.value}>
              {renderOptionLabel(option.label)}
            </Option>
          ))}
        </Select>
      )}
    </div>
  );
};

// Para (tutar) alanları: girişte 15.700.320,50 TL biçiminde görünür, forma
// yazılan değer düz sayı (15700320.5) olarak kalır. Şimdilik sunum formlarındaki
// teklif alanları: 'teklif', 'teklif[3]' ...
const PARA_ALANI_DESENI = /^teklif(\[\d+\])?$/i;

const paraInputProps = {
  ...inputNumberMoneyProps,
  formatter: (deger, bilgi) => {
    const metin = inputNumberMoneyProps.formatter(deger, bilgi);
    return metin === '' ? '' : `${metin} TL`;
  },
  // Kutudaki 'TL' eki parser'a geri geldiği için burada temizlenir; bin ayracı
  // ve virgüllü ondalık dönüşümünü ortak parser yapar.
  parser: (deger) => inputNumberMoneyProps.parser(String(deger ?? '').replace(/TL/gi, '')),
  precision: 2,
  step: 0.01,
};

//Number Field
export const FormikNumberField = React.memo(({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onBlur } = field;
  const { touched, errors } = form;

  const hasError = touched[name] && errors[name];

  const handleChange = useCallback((val) => {
    let valueToSet;
    if (val === null || val === undefined) {
      valueToSet = '';
    } else if (typeof val === 'number') {
      valueToSet = val;
    } else if (typeof val === 'string') {
      const numVal = parseFloat(val);
      valueToSet = isNaN(numVal) ? val : numVal;
    } else {
      valueToSet = val;
    }

    setFieldValue(name, valueToSet, false); 
  }, [name, setFieldValue]);

  let normalizedValue;
  if (value === '' || value === undefined || value === null) {
    normalizedValue = null;
  } else if (typeof value === 'number') {
    normalizedValue = value;
  } else if (typeof value === 'string') {
    const numVal = parseFloat(value);
    normalizedValue = isNaN(numVal) ? null : numVal;
  } else {
    normalizedValue = value;
  }

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <InputNumber
        id={name}
        name={name}
        value={normalizedValue}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={fieldConfig.placeholder}
        disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
        status={hasError ? 'error' : ''}
        style={{
          width: '100%',
          backgroundColor: darkMode ? '#262626' : '#fff',
          borderColor: darkMode ? '#434343' : '#d9d9d9',
          color: darkMode ? '#fff' : '#000'
        }}
        min={fieldConfig.min}
        max={fieldConfig.max}
        {...(PARA_ALANI_DESENI.test(name) ? paraInputProps : {})}
      />
    </div>
  );
});

//Date Field
export const FormikDateField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;
  const hasError = touched[name] && errors[name];

  const handleChange = (date, dateString) => {
    if (date) {
      const val = dayjs.isDayjs(date) ? date.format('YYYY-MM-DD') : dateString;
      setFieldValue(name, val);
    } else {
      setFieldValue(name, '');
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      form.setFieldTouched(name, true, false);
    }
  };
  
  const getDateValue = () => {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (typeof value === 'string') {
      if (['0000-00-00', 'null', 'NULL', ''].includes(value)) return null;
      
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }
    return null;
  };

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <DatePicker
        id={name}
        name={name}
        value={getDateValue()}
        onChange={handleChange}
        onOpenChange={handleOpenChange}
        placeholder={fieldConfig.placeholder}
        disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
        status={hasError ? 'error' : ''}
        style={{
          width: '100%',
          backgroundColor: darkMode ? '#262626' : '#fff',
          borderColor: darkMode ? '#434343' : '#d9d9d9',
          color: darkMode ? '#fff' : '#000'
        }}
        format="DD/MM/YYYY"
        allowClear
        getPopupContainer={() => document.body}
        styles={{
          popup: {
            root: {
              backgroundColor: darkMode ? '#1f1f1f' : '#fff',
              border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
              zIndex: 99999 
            }
          }
        }}
      />
      
      {hasError && (
        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
          {errors[name]}
        </div>
      )}
    </div>
  );
};

export const FormikDateTimeField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;
  const hasError = touched[name] && errors[name];

  const handleChange = (time, timeString) => {
    if (time && dayjs.isDayjs(time)) {
      setFieldValue(name, time.format('HH:mm:ss'));
    } else if (timeString) {
      setFieldValue(name, timeString);
    } else {
      setFieldValue(name, '');
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      form.setFieldTouched(name, true, false);
    }
  };

  const getTimeValue = () => {
    if (!value) return null;

    if (dayjs.isDayjs(value)) {
      return value;
    }

    if (typeof value === 'string') {
      if (value === '' || value === 'null' || value === 'NULL' || value.match(/^null$/i)) {
        return null;
      }
      
      if (value.match(/^\d{2}:\d{2}:\d{2}$/)) {
        const today = dayjs();
        const parsed = dayjs(`${today.format('YYYY-MM-DD')} ${value}`, 'YYYY-MM-DD HH:mm:ss');
        return parsed.isValid() ? parsed : null;
      }
      if (value.match(/^\d{2}:\d{2}$/)) {
        const today = dayjs();
        const parsed = dayjs(`${today.format('YYYY-MM-DD')} ${value}:00`, 'YYYY-MM-DD HH:mm:ss');
        return parsed.isValid() ? parsed : null;
      }
      if (value.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        const parsed = dayjs(value, 'YYYY-MM-DD HH:mm:ss');
        return parsed.isValid() ? parsed : null;
      }
    }

    return null;
  };

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <TimePicker
        id={name}
        name={name}
        value={getTimeValue()}
        onChange={handleChange}
        onOpenChange={handleOpenChange}
        placeholder={fieldConfig.placeholder || 'Saat seçin'}
        disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
        status={hasError ? 'error' : ''}
        getPopupContainer={() => document.body}
        style={{
          width: '100%',
          backgroundColor: darkMode ? '#262626' : '#fff',
          borderColor: darkMode ? '#434343' : '#d9d9d9',
          color: darkMode ? '#fff' : '#000'
        }}
        format="HH:mm"
        styles={{
          popup: {
            root: {
              backgroundColor: darkMode ? '#1f1f1f' : '#fff',
              border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
            }
          }
        }}
      />
    </div>
  );
};

export const FormikBooleanField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;

  const hasError = touched[name] && errors[name];

  const handleChange = (e) => {
    setFieldValue(name, e.target.checked ? 1 : 0);
  };

  const isChecked = () => {
    const checked = value === 1 || value === '1' || value === true;
    return checked;
  };

  return (
    <div>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
        fontWeight: 200,
        color: darkMode ? '#fff' : '#000'
      }}>
        <input
          type="checkbox"
          id={name}
          name={name}
          checked={isChecked()}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
          style={{
            marginRight: '8px',
            transform: 'scale(1.5)',
            accentColor: darkMode ? '#1890ff' : '#1890ff',
            border: '2px solid #1890ff',
            borderRadius: '3px',
            width: '12px',
            height: '12px'
          }}
        />
        <span style={{ 
          color: darkMode ? '#fff' : '#000',
          fontSize: '14px'
        }}>
          {renderLabel(fieldConfig.placeholder || fieldConfig.label)}
          {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
        </span>
      </label>
      {hasError && (
        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
          {errors[name]}
        </div>
      )}
    </div>
  );
};

//Checkbox Field
export const FormikCheckboxField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onBlur } = field;
  const { touched, errors } = form;

  const hasError = touched[name] && errors[name];

  const handleChange = useCallback((e) => {
    setFieldValue(name, e.target.checked ? 1 : 0, false);
  }, [name, setFieldValue]);

  const isChecked = () => {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    return value === 1 || value === '1' || value === true || value === 'true';
  };

  return (
    <div>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        <input
          type="checkbox"
          id={name}
          name={name}
          checked={isChecked()}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
          style={{
            marginRight: '8px',
            transform: 'scale(1.2)',
            accentColor: darkMode ? '#1890ff' : '#1890ff',
            cursor: fieldConfig.readOnly || fieldConfig.attr === "disabled" ? 'not-allowed' : 'pointer'
          }}
        />
        <span style={{ 
          color: darkMode ? '#fff' : '#000',
          fontSize: '14px'
        }}>
          {renderLabel(fieldConfig.placeholder || fieldConfig.label)}
          {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
        </span>
      </label>
      {hasError && (
        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
          {errors[name]}
        </div>
      )}
    </div>
  );
};

//File Field
export const FormikFileField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onBlur } = field;
  const { touched, errors } = form;
  const hasError = touched[name] && errors[name];
  const [fileList, setFileList] = React.useState([]);
  React.useEffect(() => {
    if (value && typeof value === 'string' && value.length > 0 && fileList.length === 0) {
      setFileList([{
        uid: '-1',
        name: 'Belge', 
        status: 'done',
      }]);
    }
  }, [value]); 

  const handleChange = ({ fileList: newFileList }) => {
    const latestFileList = newFileList.slice(-1);
    setFileList(latestFileList);
    if (latestFileList.length === 0) {
      setFieldValue(name, '');
      return;
    }

    const file = latestFileList[0];
    if (file && file.originFileObj) {
       const reader = new FileReader();
       reader.onload = (e) => {
         const base64String = e.target.result;
         setFieldValue(name, base64String);
         setFieldValue(`${name}_filename`, file.originFileObj.name || '');
         file.status = 'done';
         setFileList([...latestFileList]);
       };
       reader.onerror = () => {
         console.error('Dosya okuma hatası');
         file.status = 'error';
         setFileList([...latestFileList]);
       };
       reader.readAsDataURL(file.originFileObj);
    }
  };

  const handleRemove = () => {
    setFileList([]);
    setFieldValue(name, '');
    setFieldValue(`${name}_filename`, '');
    return true;
  };

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <Upload
        id={name}
        name={name}
        fileList={fileList} 
        onChange={handleChange}
        onRemove={handleRemove}
        onBlur={onBlur} 
        disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
        beforeUpload={() => false} 
        maxCount={1}
      >
        <Button
          style={{
            backgroundColor: darkMode ? '#262626' : '#fff',
            borderColor: darkMode ? '#434343' : '#d9d9d9',
            color: darkMode ? '#fff' : '#000',
            width: '100%'
          }}
        >
           Dosya Seç
        </Button>
      </Upload>

      {hasError && (
        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
          {errors[name]}
        </div>
      )}
    </div>
  );
};

//SQL Select Field
export const FormikSQLSelectField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;

  const hasError = touched[name] && errors[name];

  const { talepListesi, loading, error } = useTalepListesi();

  const getSelectValue = () => {
    if (!value) return undefined;

    if (name === 'kullanici_surucu') {
      if (value === 0 || value === '0') {
        return undefined;
      }
      if (typeof value === 'string' && value.includes(':')) {
        return value.split(':')[0].trim();
      }
      return value;
    }

    if (typeof value === 'string' && value.includes(',')) {
      const firstOption = value.split(',')[0];
      if (firstOption.includes(':')) {
        return firstOption.split(':')[0].trim();
      }
      return firstOption.trim();
    }

    if (typeof value === 'string' && value.includes(':')) {
      return value.split(':')[0].trim();
    }

    return value;
  };

  if (loading) {
    return (
      <div>
        <label style={{
          display: 'block',
          marginBottom: 8,
          fontWeight: 500,
          color: darkMode ? '#fff' : '#000'
        }}>
          {renderLabel(fieldConfig.label)}
          {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
        </label>
        <Select
          id={name}
          name={name}
          placeholder="Yükleniyor..."
          disabled={true}
          style={{
            width: '100%',
            backgroundColor: darkMode ? '#262626' : '#fff',
            color: darkMode ? '#fff' : '#000'
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <label style={{
          display: 'block',
          marginBottom: 8,
          fontWeight: 500,
          color: darkMode ? '#fff' : '#000'
        }}>
          {renderLabel(fieldConfig.label)}
          {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
        </label>
        <Select
          id={name}
          name={name}
          placeholder="Veri yüklenemedi"
          disabled={true}
          style={{
            width: '100%',
            backgroundColor: darkMode ? '#262626' : '#fff',
            color: darkMode ? '#fff' : '#000'
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <Select
        id={name}
        name={name}
        value={getSelectValue()}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={name === 'kullanici_surucu' && (value === 0 || value === '0') ? 'Seçiniz' : fieldConfig.placeholder}
        disabled={fieldConfig.readOnly || fieldConfig.attr === "disabled"}
        status={hasError ? 'error' : ''}
        style={{
          width: '100%',
          backgroundColor: darkMode ? '#262626' : '#fff',
          color: darkMode ? '#fff' : '#000'
        }}
        showSearch
        filterOption={(input, option) =>
          trIncludes(option?.children, input)
        }
        styles={{
          popup: {
            root: {
              backgroundColor: darkMode ? '#1f1f1f' : '#fff',
              border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
            }
          }
        }}
      >
        {(() => {
          const options = getOptionsFromValue(fieldConfig);
          if (options.length > 0) {
            return options.map((option) => (
              <Option key={option.value} value={option.value}>
                {renderOptionLabel(option.label)}
              </Option>
            ));
          }
          
          return talepListesi?.map((item) => (
            <Option key={item.key} value={item.key}>
              {renderOptionLabel(`${item.Proje} - ${item.İl} / ${item.İlçe}`)}
            </Option>
          ));
        })()}
      </Select>
    </div>
  );
};

//Year Based Cost Field
export const FormikYearBasedCostField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, uniqueValue = null, formId = null, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;

  const hasError = touched[name] && errors[name];
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [yearBasedForm] = Form.useForm();
  const [yearsData, setYearsData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const parseValue = () => {
    if (!value) return { years: [] };
    try {
      const parsed = JSON.parse(value);
      return parsed;
    } catch {
      return { years: [] };
    }
  };

  const currentData = parseValue();

  const getSozlesmeId = () => {
    if (uniqueValue) {
      return uniqueValue;
    }
    
    const formValues = form.values;
    const sozlesmeId = formValues.id || formValues.ID || formValues.sozlesme_id || formValues.sozlesmeId || formValues.sozlesmeID || null;
    return sozlesmeId;
  };

  const loadExistingData = async () => {
    const sozlesmeId = getSozlesmeId();
    if (!sozlesmeId) {
      setYearsData(currentData.years || []);
      return;
    }

    setLoading(true);
    try {
      const url = `${API_BASE_URL}/form/yil-bazli-maliyetler/${sozlesmeId}`;      
      const response = await fetch(url);      
      if (response.ok) {
        const result = await response.json();        
        if (result.success && result.data) {
          setYearsData(result.data);
        } else {
          setYearsData(currentData.years || []);
        }
      } else {
        const errorText = await response.text();
        console.error('loadExistingData - response error:', response.status, errorText);
        setYearsData(currentData.years || []);
      }
    } catch (err) {
      console.error('Yıl bazlı maliyetler yüklenirken hata:', err);
      setYearsData(currentData.years || []);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldClick = async () => {
    if (fieldConfig.attr && fieldConfig.attr.includes("disabled")) {
      return;
    }
    
    setIsModalVisible(true);
    await loadExistingData();
  };

  const addYear = () => {
    const newYear = new Date().getFullYear().toString();
    const yearExists = yearsData.find(y => y.year === newYear);
    if (yearExists) {
      Modal.warning({
        title: 'Uyarı',
        content: `${newYear} yılı zaten mevcut`,
      });
      return;
    }
    setYearsData([...yearsData, { year: newYear, entries: [] }]);
  };

  const removeYear = (yearIndex) => {
    const updated = yearsData.filter((_, i) => i !== yearIndex);
    setYearsData(updated);
  };

  const addCostEntry = (yearIndex) => {
    const updated = [...yearsData];
    updated[yearIndex].entries = [...(updated[yearIndex].entries || []), { type: 'maliyet', value: '' }];
    setYearsData(updated);
  };

  const removeCostEntry = (yearIndex, entryIndex) => {
    const updated = [...yearsData];
    updated[yearIndex].entries = updated[yearIndex].entries.filter((_, i) => i !== entryIndex);
    setYearsData(updated);
  };

  const updateCostEntry = (yearIndex, entryIndex, key, newValue) => {
    const updated = [...yearsData];
    updated[yearIndex].entries[entryIndex] = {
      ...updated[yearIndex].entries[entryIndex],
      [key]: newValue
    };
    setYearsData(updated);
  };

  const updateYear = (yearIndex, newYear) => {
    const updated = [...yearsData];
    updated[yearIndex].year = newYear;
    setYearsData(updated);
  };

  const handleYearBasedSubmit = async () => {
    try {
      for (const yearData of yearsData) {
        if (!yearData.year || yearData.year.length !== 4) {
          Modal.error({
            title: 'Hata',
            content: 'Tüm yıllar geçerli olmalıdır (4 haneli)',
          });
          return;
        }

        if (yearData.entries && yearData.entries.length > 0) {
          const hasEmptyValue = yearData.entries.some(entry => !entry.value || entry.value === '');
          if (hasEmptyValue) {
            Modal.error({
              title: 'Hata',
              content: `${yearData.year} yılı için tüm maliyet değerleri doldurulmalıdır`,
            });
            return;
          }
        }
      }

      const dataToSave = {
        years: yearsData.length > 0 ? yearsData.map(yearData => ({
          year: yearData.year,
          entries: (yearData.entries || []).map(entry => ({
            type: entry.type,
            value: parseFloat(entry.value) || 0
          }))
        })) : []
      };

      const jsonString = JSON.stringify(dataToSave);
      setFieldValue(name, jsonString);
      setIsModalVisible(false);
    } catch (err) {
      console.error('Year based cost save error:', err);
    }
  };

  const getDisplayValue = () => {
    if (currentData.years && currentData.years.length > 0) {
      const yearCount = currentData.years.length;
      const totalEntries = currentData.years.reduce((sum, y) => sum + (y.entries?.length || 0), 0);
      return `${yearCount} yıl, ${totalEntries} maliyet kaydı`;
    }
    return fieldConfig.placeholder || 'Yıl bazlı maliyet girişi için tıklayın';
  };

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>

      <div
        onClick={handleFieldClick}
        style={{
          border: hasError ? '1px solid #ff4d4f' : `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
          borderRadius: '6px',
          padding: '8px 12px',
          backgroundColor: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? (darkMode ? '#1a1a1a' : '#f5f5f5') : (darkMode ? '#262626' : '#fff'),
          color: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? '#999' : (darkMode ? '#fff' : '#000'),
          cursor: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? 'not-allowed' : 'pointer',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          opacity: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? 0.6 : 1
        }}
      >
        <span style={{
          color: getDisplayValue() ? (darkMode ? '#fff' : '#000') : '#999',
          flex: 1
        }}>
          {getDisplayValue()}
        </span>
        {(!fieldConfig.attr || !fieldConfig.attr.includes("disabled")) && (
          <span style={{
            color: '#999',
            marginLeft: '8px'
          }}>
            ▼
          </span>
        )}
      </div>

      {/* Yıl Bazlı Maliyet Modal */}
      {isModalVisible && (
        <Modal
          title="Yıl Bazlı Maliyet Girişi"
          open={isModalVisible}
          onOk={handleYearBasedSubmit}
          onCancel={() => {
            setIsModalVisible(false);
            setYearsData([]);
          }}
          okText="Kaydet"
          cancelText="İptal"
          width={800}
          styles={{
            body: {
              backgroundColor: darkMode ? '#1f1f1f' : '#fff',
              maxHeight: '70vh',
              overflowY: 'auto'
            }
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: darkMode ? '#999' : '#666' }}>
                Yıl bazlı maliyetler yükleniyor...
              </div>
            </div>
          ) : (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 16
              }}>
                <span style={{ 
                  fontWeight: 500, 
                  fontSize: '16px',
                  color: darkMode ? '#fff' : '#000' 
                }}>
                  Yıllar ve Maliyet Tipleri
                </span>
                <Button 
                  type="dashed" 
                  onClick={addYear}
                  icon={<PlusOutlined />}
                >
                  Yıl Ekle
                </Button>
              </div>

              {yearsData.length === 0 ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  border: `1px dashed ${darkMode ? '#434343' : '#d9d9d9'}`,
                  borderRadius: '4px',
                  color: darkMode ? '#999' : '#999'
                }}>
                  Henüz yıl eklenmedi. "Yıl Ekle" butonuna tıklayarak ekleyin.
                </div>
              ) : (
                <div style={{ 
                  maxHeight: '500px', 
                  overflowY: 'auto'
                }}>
                  {yearsData.map((yearData, yearIndex) => (
                    <div 
                      key={yearIndex}
                      style={{
                        border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                        borderRadius: '6px',
                        padding: '16px',
                        marginBottom: yearIndex < yearsData.length - 1 ? '16px' : 0,
                        backgroundColor: darkMode ? '#262626' : '#fafafa'
                      }}
                    >
                      {/* Yıl Başlığı */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Input
                            value={yearData.year}
                            onChange={(e) => {
                              const newYear = e.target.value;
                              if (/^\d{0,4}$/.test(newYear)) {
                                updateYear(yearIndex, newYear);
                              }
                            }}
                            placeholder="Yıl (örn: 2024)"
                            maxLength={4}
                            style={{ 
                              width: '100px',
                              backgroundColor: darkMode ? '#1f1f1f' : '#fff',
                              color: darkMode ? '#fff' : '#000'
                            }}
                          />
                          <span style={{ 
                            fontSize: '12px', 
                            color: darkMode ? '#999' : '#666' 
                          }}>
                            ({yearData.entries?.length || 0} maliyet tipi)
                          </span>
                        </div>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeYear(yearIndex)}
                          size="small"
                        >
                          Yılı Sil
                        </Button>
                      </div>

                      {/* Maliyet Tipleri */}
                      <div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{ 
                            fontWeight: 500, 
                            fontSize: '14px',
                            color: darkMode ? '#fff' : '#000' 
                          }}>
                            Maliyet Tipleri ve Değerleri
                          </span>
                          <Button 
                            type="dashed" 
                            onClick={() => addCostEntry(yearIndex)}
                            icon={<PlusOutlined />}
                            size="small"
                          >
                            Ekle
                          </Button>
                        </div>

                        {(!yearData.entries || yearData.entries.length === 0) ? (
                          <div style={{
                            padding: '16px',
                            textAlign: 'center',
                            border: `1px dashed ${darkMode ? '#434343' : '#d9d9d9'}`,
                            borderRadius: '4px',
                            color: darkMode ? '#999' : '#999',
                            fontSize: '12px'
                          }}>
                            Bu yıl için henüz maliyet tipi eklenmedi.
                          </div>
                        ) : (
                          <div>
                            {yearData.entries.map((entry, entryIndex) => (
                              <div 
                                key={entryIndex}
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  marginBottom: entryIndex < yearData.entries.length - 1 ? '8px' : 0,
                                  paddingBottom: entryIndex < yearData.entries.length - 1 ? '8px' : 0,
                                  borderBottom: entryIndex < yearData.entries.length - 1 ? `1px solid ${darkMode ? '#434343' : '#f0f0f0'}` : 'none'
                                }}
                              >
                                <Select
                                  value={entry.type}
                                  onChange={(val) => updateCostEntry(yearIndex, entryIndex, 'type', val)}
                                  style={{ width: '150px' }}
                                  options={[
                                    { label: 'Maliyet', value: 'maliyet' },
                                    { label: 'Elektrik', value: 'elektrik' },
                                    { label: 'Kira', value: 'kira' },
                                    { label: 'Ortak Alan', value: 'ortak_alan' },
                                    { label: 'Boş', value: 'bos' },
                                    { label: 'Kabin', value: 'kabin' }
                                  ]}
                                />
                                <InputNumber
                                  value={entry.value}
                                  onChange={(val) => updateCostEntry(yearIndex, entryIndex, 'value', val)}
                                  placeholder="Değer"
                                  min={0}
                                  step={0.01}
                                  style={{ flex: 1 }}
                                  {...inputNumberMoneyProps}
                                />
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeCostEntry(yearIndex, entryIndex)}
                                  size="small"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

//Hisseler Field
export const FormikHisselerField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, uniqueValue = null, formId = null, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;
  const hasError = touched[name] && errors[name];
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [hisselerData, setHisselerData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [nfCariOptions, setNfCariOptions] = React.useState([]);
  const [loadingNfCari, setLoadingNfCari] = React.useState(false);

  const parseValue = () => {
    if (!value) return { hisseler: [] };
    try {
      const parsed = JSON.parse(value);
      return parsed;
    } catch {
      return { hisseler: [] };
    }
  };

  const currentData = parseValue();

  const getSozlesmeId = () => {
    if (uniqueValue) {
      return uniqueValue;
    }
  
    const formValues = form.values;
    const sozlesmeId = formValues.id || formValues.ID || formValues.sozlesme_id || formValues.sozlesmeId || formValues.sozlesmeID || null;
    return sozlesmeId;
  };

  const loadNfCariOptions = async () => {
    if (nfCariOptions.length > 0) return; 
    
    setLoadingNfCari(true);
    try {
      const response = await fetch(`${API_BASE_URL}/form/nf-cari-listesi`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setNfCariOptions(result.data.map(item => ({
            label: item.ad || item.name || `${item.id}`,
            value: item.id,
            ...item
          })));
        }
      }
    } catch (err) {
      console.error('nf_cari listesi yüklenirken hata:', err);
    } finally {
      setLoadingNfCari(false);
    }
  };

  const loadExistingData = async () => {
    const sozlesmeId = getSozlesmeId();
    
    if (!sozlesmeId) {
      setHisselerData(currentData.hisseler || []);
      return;
    }

    setLoading(true);
    try {
      const url = `${API_BASE_URL}/form/hisseler/${sozlesmeId}`;      
      const response = await fetch(url);      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.data) {
          const formattedData = result.data.map(item => ({
            hisse_sira: item.hisse_sira || item.hisseSira || '',
            hisse_oran: item.hisse_oran || item.hisseOran || '',
            nf_cari_id: item.nf_cari_id || item.nfCariId || null,
            nf_cari_ad: item.nf_cari_ad || item.nfCariAd || ''
          }));
          setHisselerData(formattedData);
        } else {
          setHisselerData(currentData.hisseler || []);
        }
      } else {
        const errorText = await response.text();
        console.error('loadExistingData - response error:', response.status, errorText);
        setHisselerData(currentData.hisseler || []);
      }
    } catch (err) {
      console.error('Hisseler yüklenirken hata:', err);
      setHisselerData(currentData.hisseler || []);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldClick = async () => {
    if (fieldConfig.attr && fieldConfig.attr.includes("disabled")) {
      return;
    }
    
    setIsModalVisible(true);
    await loadNfCariOptions();
    await loadExistingData();
  };

  const addHisse = () => {
    setHisselerData([...hisselerData, { hisse_sira: '', hisse_oran: '', nf_cari_id: null, nf_cari_ad: '' }]);
  };

  const removeHisse = (hisseIndex) => {
    const updated = hisselerData.filter((_, i) => i !== hisseIndex);
    setHisselerData(updated);
  };

  const updateHisse = (hisseIndex, key, newValue) => {
    const updated = [...hisselerData];
    if (key === 'nf_cari_id') {
      const selectedCari = nfCariOptions.find(opt => opt.value === newValue);
      updated[hisseIndex] = {
        ...updated[hisseIndex],
        nf_cari_id: newValue,
        nf_cari_ad: selectedCari ? selectedCari.label : ''
      };
    } else {
      updated[hisseIndex] = {
        ...updated[hisseIndex],
        [key]: newValue
      };
    }
    setHisselerData(updated);
  };

  const handleHisselerSubmit = async () => {
    try {
      for (const hisse of hisselerData) {
        if (!hisse.hisse_sira || hisse.hisse_sira === '') {
          Modal.error({
            title: 'Hata',
            content: 'Tüm hisse sıraları doldurulmalıdır',
          });
          return;
        }

        if (!hisse.hisse_oran || hisse.hisse_oran === '') {
          Modal.error({
            title: 'Hata',
            content: 'Tüm hisse oranları doldurulmalıdır',
          });
          return;
        }

        if (!hisse.nf_cari_id) {
          Modal.error({
            title: 'Hata',
            content: 'Tüm hisse sahipleri seçilmelidir',
          });
          return;
        }
      }

      const dataToSave = {
        hisseler: hisselerData.map(hisse => ({
          hisse_sira: hisse.hisse_sira,
          hisse_oran: parseFloat(hisse.hisse_oran) || 0,
          nf_cari_id: hisse.nf_cari_id
        }))
      };

      const jsonString = JSON.stringify(dataToSave);
      setFieldValue(name, jsonString);
      setIsModalVisible(false);
    } catch (err) {
      console.error('Hisseler kaydedilirken hata:', err);
    }
  };

  const getDisplayValue = () => {
    if (currentData.hisseler && currentData.hisseler.length > 0) {
      const hisseCount = currentData.hisseler.length;
      return `${hisseCount} hisse kaydı`;
    }
    return fieldConfig.placeholder || 'Hisse girişi';
  };

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>

      <div
        onClick={handleFieldClick}
        style={{
          border: hasError ? '1px solid #ff4d4f' : `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
          borderRadius: '6px',
          padding: '8px 12px',
          backgroundColor: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? (darkMode ? '#1a1a1a' : '#f5f5f5') : (darkMode ? '#262626' : '#fff'),
          color: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? '#999' : (darkMode ? '#fff' : '#000'),
          cursor: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? 'not-allowed' : 'pointer',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          opacity: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? 0.6 : 1
        }}
      >
        <span style={{
          color: getDisplayValue() ? (darkMode ? '#fff' : '#000') : '#999',
          flex: 1
        }}>
          {getDisplayValue()}
        </span>
        {(!fieldConfig.attr || !fieldConfig.attr.includes("disabled")) && (
          <span style={{
            color: '#999',
            marginLeft: '8px'
          }}>
            ▼
          </span>
        )}
      </div>

      {/* Hisseler Modal */}
      {isModalVisible && (
        <Modal
          title="Hisseler Girişi"
          open={isModalVisible}
          onOk={handleHisselerSubmit}
          onCancel={() => {
            setIsModalVisible(false);
            setHisselerData([]);
          }}
          okText="Kaydet"
          cancelText="İptal"
          width={800}
          styles={{
            body: {
              backgroundColor: darkMode ? '#1f1f1f' : '#fff',
              maxHeight: '70vh',
              overflowY: 'auto'
            }
          }}
        >
          {loading || loadingNfCari ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: darkMode ? '#999' : '#666' }}>
                {loading ? 'Hisseler yükleniyor...' : 'Cari listesi yükleniyor...'}
              </div>
            </div>
          ) : (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 16
              }}>
                <span style={{ 
                  fontWeight: 500, 
                  fontSize: '16px',
                  color: darkMode ? '#fff' : '#000' 
                }}>
                  Hisseler
                </span>
                <Button 
                  type="dashed" 
                  onClick={addHisse}
                  icon={<PlusOutlined />}
                >
                  Hisse Ekle
                </Button>
              </div>

              {hisselerData.length === 0 ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  border: `1px dashed ${darkMode ? '#434343' : '#d9d9d9'}`,
                  borderRadius: '4px',
                  color: darkMode ? '#999' : '#999'
                }}>
                  Henüz hisse eklenmedi. "Hisse Ekle" butonuna tıklayarak ekleyin.
                </div>
              ) : (
                <div style={{ 
                  maxHeight: '500px', 
                  overflowY: 'auto'
                }}>
                  {hisselerData.map((hisse, hisseIndex) => (
                    <div 
                      key={hisseIndex}
                      style={{
                        border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                        borderRadius: '6px',
                        padding: '16px',
                        marginBottom: hisseIndex < hisselerData.length - 1 ? '16px' : 0,
                        backgroundColor: darkMode ? '#262626' : '#fafafa'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <span style={{ 
                          fontWeight: 500, 
                          fontSize: '14px',
                          color: darkMode ? '#fff' : '#000' 
                        }}>
                          Hisse #{hisseIndex + 1}
                        </span>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeHisse(hisseIndex)}
                          size="small"
                        >
                          Sil
                        </Button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '4px',
                            fontSize: '12px',
                            color: darkMode ? '#999' : '#666' 
                          }}>
                            Hisse Sırası
                          </label>
                          <Input
                            value={hisse.hisse_sira}
                            onChange={(e) => updateHisse(hisseIndex, 'hisse_sira', e.target.value)}
                            placeholder="Hisse sırası (örn: 1, 2, 3...)"
                            style={{ 
                              backgroundColor: darkMode ? '#1f1f1f' : '#fff',
                              color: darkMode ? '#fff' : '#000'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '4px',
                            fontSize: '12px',
                            color: darkMode ? '#999' : '#666' 
                          }}>
                            Hisse Oranı
                          </label>
                          <InputNumber
                            value={hisse.hisse_oran}
                            onChange={(val) => updateHisse(hisseIndex, 'hisse_oran', val)}
                            placeholder="Hisse oranı"
                            min={0}
                            max={100}
                            step={0.01}
                            style={{ width: '100%' }}
                            formatter={value => value ? `${value}%` : ''}
                            parser={value => value.replace('%', '')}
                          />
                        </div>

                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '4px',
                            fontSize: '12px',
                            color: darkMode ? '#999' : '#666' 
                          }}>
                            Hisse Sahibi (nf_cari)
                          </label>
                          <Select
                            value={hisse.nf_cari_id}
                            onChange={(val) => updateHisse(hisseIndex, 'nf_cari_id', val)}
                            placeholder="Hisse sahibini seçin"
                            showSearch
                            filterOption={(input, option) =>
                              trIncludes(option?.label ?? '', input)
                            }
                            style={{ width: '100%' }}
                            options={nfCariOptions}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

//Table Field
export const FormikTableField = ({ field, form, fieldConfig, setFieldValue, darkMode = false, ...props }) => {
  const { name, value, onChange, onBlur } = field;
  const { touched, errors } = form;

  const hasError = touched[name] && errors[name];
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);
  const [totalRecords, setTotalRecords] = React.useState(0);

  const executeQuery = async (page = 1, limit = 50) => {
    if (!fieldConfig.query) {
      setError('Query not found in field configuration');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = fieldConfig.query.trim().replace(/;$/, '');
      const placeholderRegex = /\{\{(\w+)\}\}/g;
      let match;
      while ((match = placeholderRegex.exec(query)) !== null) {
        const placeholderName = match[1];
        const fieldValue = form.values[placeholderName];
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          query = query.replace(new RegExp(`\\{\\{${placeholderName}\\}\\}`, 'g'), fieldValue);
        } else {
          console.warn(`Placeholder {{${placeholderName}}} has no value, replacing with 0`);
          query = query.replace(new RegExp(`\\{\\{${placeholderName}\\}\\}`, 'g'), '0');
        }
      }
      
      const cleanQuery = query;
      let paginatedQuery;
      if (cleanQuery.toUpperCase().includes('GROUP BY')) {
        paginatedQuery = `SELECT * FROM (${cleanQuery}) as wrapped_query LIMIT ${limit} OFFSET ${(page - 1) * limit}`;
      } else {
        paginatedQuery = `${cleanQuery} LIMIT ${limit} OFFSET ${(page - 1) * limit}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/form/execute-sql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sql: paginatedQuery
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTableData(data);
      setCurrentPage(page);
      
      if (page === 1) {
        try {
          const countResponse = await fetch(`${API_BASE_URL}/form/execute-sql`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sql: `SELECT COUNT(*) as total FROM (${cleanQuery}) as count_query`
            })
          });
          
          if (countResponse.ok) {
            const countData = await countResponse.json();
            setTotalRecords(countData[0]?.total || data.length);
          }
        } catch (countErr) {
          console.warn('Count query failed:', countErr);
          setTotalRecords(data.length);
        }
      }
    } catch (err) {
      console.error('Query execution error for field', fieldConfig.name, ':', err);
      setError(err.message || 'Query execution failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldClick = () => {    
    if (fieldConfig.attr && fieldConfig.attr.includes("disabled")) {
      return;
    }
    
    setIsModalVisible(true);
    if (tableData.length === 0 && !loading) {
      executeQuery();
    }
  };

  const handleRowSelect = (row) => {
    setSelectedRow(row);
    const displayValue = Object.values(row).join(' - ');
    const keyValue = row.key || row.id || row.ID || Object.values(row)[0];
    setFieldValue(name, keyValue);
    setIsModalVisible(false);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setError(null);
  };

  const getDisplayValue = () => {
    if (selectedRow) {
      return Object.values(selectedRow).join(' - ');
    }
    return value || '';
  };

  return (
    <div>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 500,
        color: darkMode ? '#fff' : '#000'
      }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>

      <div
        onClick={handleFieldClick}
        style={{
          border: hasError ? '1px solid #ff4d4f' : `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
          borderRadius: '6px',
          padding: '8px 12px',
          backgroundColor: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? (darkMode ? '#1a1a1a' : '#f5f5f5') : (darkMode ? '#262626' : '#fff'),
          color: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? '#999' : (darkMode ? '#fff' : '#000'),
          cursor: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? 'not-allowed' : 'pointer',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          opacity: (fieldConfig.attr && fieldConfig.attr.includes("disabled")) ? 0.6 : 1
        }}
      >
        <span style={{
          color: getDisplayValue() ? (darkMode ? '#fff' : '#000') : '#999',
          flex: 1
        }}>
          {getDisplayValue() || fieldConfig.placeholder || 'Tabloyu açmak için tıklayın'}
        </span>
        {(!fieldConfig.attr || !fieldConfig.attr.includes("disabled")) && (
          <span style={{
            color: '#999',
            marginLeft: '8px'
          }}>
            ▼
          </span>
        )}
      </div>

      {/* Table Modal */}
      {isModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: darkMode ? '#1f1f1f' : '#fff',
            borderRadius: '8px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                color: darkMode ? '#fff' : '#000',
                fontSize: '16px',
                fontWeight: 500
              }}>
                {fieldConfig.label}
              </h3>
              <button
                onClick={handleModalClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: darkMode ? '#fff' : '#000',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '20px',
              flex: 1,
              overflow: 'auto'
            }}>
              {loading && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: darkMode ? '#fff' : '#000'
                }}>
                  Yükleniyor...
                </div>
              )}

              {error && (
                <div style={{
                  color: '#ff4d4f',
                  padding: '16px',
                  backgroundColor: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '6px',
                  marginBottom: '16px'
                }}>
                  Hata: {error}
                </div>
              )}

              {!loading && !error && tableData.length > 0 && (
                <div style={{ overflow: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{
                        backgroundColor: darkMode ? '#262626' : '#fafafa',
                        borderBottom: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
                      }}>
                        {Object.keys(tableData[0]).map((column, index) => (
                          <th key={index} style={{
                            padding: '12px 8px',
                            textAlign: 'left',
                            fontWeight: 500,
                            color: darkMode ? '#fff' : '#000',
                            borderRight: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
                          }}>
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, rowIndex) => {
                        const columns = Object.keys(row);
                        const lastColumn = columns[columns.length - 1];
                        const lastColumnValue = row[lastColumn];

                        return (
                          <tr
                            key={rowIndex}
                            style={{
                              borderBottom: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                              backgroundColor: selectedRow === row ? (darkMode ? '#1890ff' : '#e6f7ff') : 'transparent'
                            }}
                          >
                            {Object.entries(row).map(([columnName, cellValue], cellIndex) => {
                              const isLastColumn = cellIndex === Object.keys(row).length - 1;

                              return (
                                <td
                                  key={cellIndex}
                                  onClick={() => handleRowSelect(row)}
                                  style={{
                                    padding: '12px 8px',
                                    color: darkMode ? '#fff' : '#000',
                                    borderRight: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                                    cursor: 'pointer',
                                    position: 'relative'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (selectedRow !== row) {
                                      e.target.style.backgroundColor = darkMode ? '#262626' : '#f5f5f5';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (selectedRow !== row) {
                                      e.target.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {isLastColumn ? (
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowSelect(row);
                                          }}
                                          style={{
                                            padding: '2px 6px',
                                            fontSize: '10px',
                                            backgroundColor: '#1890ff',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '3px',
                                            cursor: 'pointer',
                                            minWidth: '40px'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#40a9ff';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = '#1890ff';
                                          }}
                                        >
                                          Seç
                                        </button>
                                      </div>
                                    ) : (
                                      <span>
                                        {cellValue !== null && cellValue !== undefined ? String(cellValue) : ''}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && tableData.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: darkMode ? '#fff' : '#000'
                }}>
                  Veri bulunamadı
                </div>
              )}

              {/* Pagination Controls */}
              {!loading && !error && tableData.length > 0 && totalRecords > pageSize && (
                <div style={{
                  padding: '16px 20px',
                  borderTop: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ color: darkMode ? '#fff' : '#000' }}>
                    {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRecords)} / {totalRecords} kayıt
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => executeQuery(currentPage - 1, pageSize)}
                      disabled={currentPage === 1 || loading}
                      style={{
                        padding: '6px 12px',
                        border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                        backgroundColor: darkMode ? '#262626' : '#fff',
                        color: darkMode ? '#fff' : '#000',
                        borderRadius: '4px',
                        cursor: currentPage === 1 || loading ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 || loading ? 0.5 : 1
                      }}
                    >
                      Önceki
                    </button>
                    <span style={{
                      padding: '6px 12px',
                      color: darkMode ? '#fff' : '#000',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {currentPage} / {Math.ceil(totalRecords / pageSize)}
                    </span>
                    <button
                      onClick={() => executeQuery(currentPage + 1, pageSize)}
                      disabled={currentPage >= Math.ceil(totalRecords / pageSize) || loading}
                      style={{
                        padding: '6px 12px',
                        border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                        backgroundColor: darkMode ? '#262626' : '#fff',
                        color: darkMode ? '#fff' : '#000',
                        borderRadius: '4px',
                        cursor: currentPage >= Math.ceil(totalRecords / pageSize) || loading ? 'not-allowed' : 'pointer',
                        opacity: currentPage >= Math.ceil(totalRecords / pageSize) || loading ? 0.5 : 1
                      }}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Konum (GPS) Field — kabin taşımada (form 75) cihazdan konum alır.
// Değer "enlem,boylam" biçiminde `name` alanına yazılır; ayrıca kardeş
// kolonlar (enlem, boylam, konum_dogruluk, konum_kaynak, konum_tarihi) da
// doldurulur — bunlar kabin_tasima tablosunda karşılığı olan kolonlardır.
// Elle koordinat girişi YOK: taşıma yalnızca kabinin 100 m yakınından yapılır
// ve sunucu yalnızca GPS kaynaklı konumu kabul eder (services/konum_dogrulama.py).
const FormikKonumFieldComponent = ({ field, form, fieldConfig, setFieldValue, darkMode = false, extraValues = {} }) => {
  const { name, value, onBlur } = field;
  const { touched, errors, values } = form;
  const hasError = touched[name] && errors[name];
  // Form 75'te `kabin` gizli alan (tip 7) → şemada yok, yalnızca extraData'da gelir.
  const kabin = values?.kabin || extraValues?.kabin;
  const [durum, setDurum] = React.useState('idle'); // idle|loading|ok|error
  const [mesaj, setMesaj] = React.useState('');

  const konumAl = useCallback(async () => {
    setDurum('loading');
    setMesaj('');
    let konum;
    try {
      konum = await cihazKonumuAl();
    } catch (err) {
      setDurum('error');
      setMesaj(err.message);
      return;
    }
    setFieldValue(name, `${konum.enlem.toFixed(7)},${konum.boylam.toFixed(7)}`, false);
    Object.entries(konum).forEach(([k, v]) => setFieldValue(k, v, false));
    const kontrol = await konumKontrol(konum, { kabin });
    if (kontrol) {
      setDurum(kontrol.gecerli ? 'ok' : 'error');
      setMesaj(kontrol.mesaj);
    } else {
      setDurum('ok');
      setMesaj(`Konum alındı (±${konum.konum_dogruluk} m)`);
    }
  }, [name, setFieldValue, kabin]);

  return (
    <div>
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: darkMode ? '#fff' : '#000' }}>
        {renderLabel(fieldConfig.label)}
        {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          id={name}
          name={name}
          value={value || ''}
          onBlur={onBlur}
          readOnly
          placeholder="'Konumu Al' ile doldurulur"
          status={hasError ? 'error' : ''}
          style={{
            backgroundColor: darkMode ? '#262626' : '#fff',
            borderColor: darkMode ? '#434343' : '#d9d9d9',
            color: darkMode ? '#fff' : '#000',
          }}
        />
        <Button onClick={konumAl} loading={durum === 'loading'}
          disabled={fieldConfig.readOnly || fieldConfig.attr === 'disabled'}>
          Konumu Al
        </Button>
      </div>
      {mesaj && (
        <div style={{ marginTop: 4, fontSize: 12, color: durum === 'error' ? '#ff4d4f' : '#52c41a' }}>
          {mesaj}
        </div>
      )}
      {value && (
        <div style={{ marginTop: 4, fontSize: 12 }}>
          <a href={`https://www.google.com/maps?q=${value}`} target="_blank" rel="noreferrer">
            Haritada göster
          </a>
        </div>
      )}
    </div>
  );
};

export const FormikKonumField = React.memo(FormikKonumFieldComponent);
